"use client";
import { LazyRichText } from "@app/_blocks/RichText/LazyRichText";
import { useCartState, useMarket, useSiteInfo } from "@app/_context";

import { MADE_TO_ORDER_CHECKOUT_TEXT_BLOCK_NAME } from "@/utils/const";
import { isMadeToOrderCartItem } from "@/utils/product/isMadeToOrderCartItem";

import { useCheckoutContext } from "./Checkout.hooks";

/**
 * "1B" made-to-order disclaimer — Piece B: a discreet, always-visible paragraph
 * below the cart lines explaining that the order ships together once every item
 * is produced. The per-item ship date lives on the item line itself (Piece A);
 * this paragraph carries no date. Copy is a Contentful RichText block so editors
 * own the wording and the inline "kundtjänst" link per market.
 */
export const CheckoutMadeToOrderLabel = () => {
  const { cart } = useCartState();
  const { madeToOrderBlock } = useCheckoutContext();
  const { market } = useMarket().state;
  const { showCheckoutMadeToOrderDisclaimer } = useSiteInfo();

  const hasMadeToOrderItems = cart?.items?.some((item) =>
    isMadeToOrderCartItem(item),
  );
  const textblock = madeToOrderBlock?.textBlockCollection?.items?.[0];

  // SiteInfo kill-switch (stakeholder-controlled): when off, the whole
  // disclaimer is suppressed. It's a positive "show" flag, so null/false hides.
  if (!showCheckoutMadeToOrderDisclaimer || !hasMadeToOrderItems) {
    return null;
  }

  // Thin grey row. The root is full width so the checkout's `divide-y` draws its
  // top/bottom dividers edge-to-edge (a `max-w` here would clip the top border);
  // `px-5` matches the summary's inset below. The readable width is capped on the
  // inner wrapper. The RichText paragraph carries no size of its own, so the text
  // styling cascades into it; `[&_a]` styles the editor-embedded kundtjänst link
  // to inherit the grey and underline (with a subtle hover).
  return (
    <div className="px-5 py-3.5 text-[12.5px] leading-[1.6] text-[#9a9893] [&_a:hover]:opacity-70 [&_a]:text-inherit [&_a]:underline">
      <div className="max-w-[720px]">
        {textblock?.content ? (
          <LazyRichText
            content={textblock.content}
            locale={market.locale}
            market={market.code}
            className="not-prose"
          />
        ) : (
          // Missing-value convention (`!key`): surface the Contentful TextBlock +
          // field we're looking for so it can be tracked down and published.
          <p className="not-prose">
            {`!${MADE_TO_ORDER_CHECKOUT_TEXT_BLOCK_NAME}.content`}
          </p>
        )}
      </div>
    </div>
  );
};
